import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { alertActions, filterActions, preAlertActions } from '_store';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
    TextField,
    Checkbox,
    Typography,
    IconButton,
    InputLabel,
    FormControlLabel,
    Slider,
} from '@mui/material';
import { renderTimeViewClock } from '@mui/x-date-pickers';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { getIcons, history } from '_helpers';
import moment from 'moment-timezone';
import { MultiSelectAutocomplete } from '_components';
import 'moment/locale/fr';

export { FilterCreateForm };

/**
 * Formulaire de création de filtre
 * 2 cas de figure: création et modification
 * Prise en compte de la présence d'un paramètre pour différencier les 2 cas de figure
 * La gestion du formulaire se fait via react-hook-form et Yup
 * @returns 
 */
function FilterCreateForm () {
    const { filterid } = useParams();
    const dispatch = useDispatch();

    /* Récupération des éléments du store redux */
    const filters = useSelector(state => state.filters?.list);
    const filter = useSelector(state => state.filters?.item);
    const filterValues = useSelector(state => state.filters?.values);
    const preAlerts = useSelector(state => state.preAlerts?.list);

    /* Mise en forme des préalertes pour l'affichage dans le champ de sélection */
    const [preAlertsList, setPreAlertsList] = useState();
    useEffect(() => {
        if(preAlerts){
            const values = Object.entries(preAlerts).map(([key, value]) => {
                return `${value.name} (${key})`;
            });
            setPreAlertsList(values);
        }
    }, [preAlerts]);

    /* Sections utilisée pour fractionner l'affichage (afficher/masquer des zones) */
    const [sections, setSections] = useState({
        generalInfo: true,
        when: true,
        criteria: true,
        alert: true,
    });    

    /* Gestion du mode Edition / Création */
    const [isEditMode, setIsEditMode] = useState(false);
    const [ title, setTitle ] = useState('Créer un filtre');

    /* Variables de Temps et de zone 
    La sélection de date dois se faire entre il y a un mois et aujourd'hui */
    const timezone = 'Europe/Paris';
    const oneMonthAgoMidnight = moment().subtract(1, 'month').startOf('day');
    const todayMidnight = moment().startOf('day');

    /* Variables min et max et Mise en forme du texte du slider */
    const valuetext = (value) => {
        return `${value}%`;
    }

    const marks = [{ value: 0, label: '0%'},{value: 100, label: '100%'}];    
    
    /* Schema de validation Yup */
    const validationSchema = yup.object().shape({
        generalInfo: yup.object().shape({
            name: yup.string().required('Nom requis'),
            description: yup.string().required('Description requise'),
        }),
    });

    /* Gestion du formulaire et initialisation */
    const { control, handleSubmit, setValue, getValues, register, reset, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            generalInfo: {name: '', description: ''},
            when: { startDate: null, endDate: null },
            criteria: {                
                COUNTRY: [], CITY: [], busType: [], BATTERY_MODEL: [], PACKS_NB: [], ECU_STATIC: [],
                URGENCY_LEVEL: [], SOC: [0,0] ,
                PREALERTE: [],
            }
        },
    });    

    /******  Hooks d'événements ******/
    //Récupération des filtres
    useEffect(() => {
        if (!filters?.list && filterid) {
            dispatch(filterActions.getAllFilters());            
        }
        if (!filterValues){
            dispatch(filterActions.getFilterValues());
        }
        if(!preAlerts){
            dispatch(preAlertActions.getAll());
        }
    }, []);

    //Différenciation du mode Edition / création
    useEffect(() => {    
        if (filterid && filters) {
            setIsEditMode(true);
            if (!filter) {
                const filter = filters[filterid];
                dispatch(filterActions.setFilter(filter))
                initializeForm(filter);
            } else {
                initializeForm(filter);
            }
        }
    }, [filterid, filters, filter, dispatch]);

    useEffect(() => {
        if (isEditMode){
            setTitle('Modifier le filtre')
        }
    }, [isEditMode])

    /**
     * Initialisation du formulaire en fonction des données récupérées en mode modification
     * @param {*} filter 
     */
    const initializeForm = (filter) => {
        if (filter) {
            const filterToPopulate = {}
            setValue('generalInfo.name', filter.name);
            setValue('generalInfo.description', filter.description);

            const when = {} //Gestion des dates            
            const criteria = []; //Les autres critères
            const criteriaFields = ['COUNTRY', 'CITY', 'busType', 
                'BATTERY_MODEL', 'PACKS_NB', 'ECU_STATIC',
                'URGENCY_LEVEL', 'SOC' , 'PREALERTE']

            /* Différenciation des types de champ,
            Les règles d'application diffèrent selon le type */
            filter.rules.forEach(rule => {           
                if (rule.champ === 'HEADER_EVTDATE') {
                    //Configuration du format de date à passer au composant MUI DateTimePicker
                    //la rgèle arrive soit avec un opérateur '>' ou '>' et une seule date
                    //soit avec un opérateur 'compris entre' et dans ce cas la deux dates
                    if (rule.operateur === 'entre') {
                        const dateDebut = moment.tz(rule.valeur?.min, 'YYYY-MM-DD HH:mm:ss', timezone);
                        const dateFin = moment.tz(rule.valeur?.max, 'YYYY-MM-DD HH:mm:ss', timezone);
                        if (dateDebut.isValid() && dateFin.isValid()) {
                            when.startDate = moment(dateDebut);
                            when.endDate = moment(dateFin);
                        }
                    } else {
                        const date = moment.tz(rule.valeur, 'YYYY-MM-DD HH:mm:ss', timezone);
                        if (date.isValid()) {
                            if (rule.operateur === '>') {
                                when.startDate = moment(date);
                            } else if (rule.operateur === '<') {
                                when.endDate = moment(date);
                            }
                        } else {
                            console.error(`Invalid date: ${rule.valeur}`);
                        }
                    }
                    
                } else if (rule.operateur === 'compris entre'){
                    //Cas spécifique oparateur 'compris entre' pour les autres champs
                    let valMin = rule.valeur?.min,
                        valMax = rule.valeur?.max;
                    criteria[rule.champ] = [valMin, valMax];                    
                } else if (criteriaFields.includes(rule.champ)){
                    if(rule.champ === 'PREALERTE'){
                        //Cas des préalertes, récupère le nom en fonction de l'id pour formatter la valeur pour le champ multiselect
                        const formattedArray = rule.valeur.map(key => {
                            const name = preAlerts[key]?.name; // Récupère le name en fonction de la clé
                            return name ? `${name} (${key})` : null; // Retourne la chaîne formatée si le name existe
                        }).filter(item => item !== null); // Filtre les valeurs null si une clé ne correspond pas
                         criteria[rule.champ] = formattedArray;
                    } else {
                        criteria[rule.champ] = rule.valeur;
                    }                    
                }
            });

            if (when.startDate || when.endDate){
                setValue('when', when);//On initialise que si la valeur est définie précédemment
            }
            setValue('criteria', criteria);
            
            filterToPopulate.generalInfo = {name: filter.name, description:  filter.description};
            filterToPopulate.when = when;
            filterToPopulate.criteria = criteria;

            reset(filterToPopulate);
        }
    };

    /**
     * Gestion de la soumission du formulaire 
     * On rempli les éléments principaux puis on passe en revue les règles
     * pour appliquer des formats spécifiques en fonction du type
     * @param {*} data 
     */ 
    async function onSubmit(data) {
        dispatch(alertActions.clear());
        let message;
        try {
            const { generalInfo, when, criteria } = data;

            const rules = [];

            const addRule = (champ, valeur, operateur) => {
                if(champ === 'SOC' && valeur[0] === 0 && valeur[1] === 0) return                
                if(champ === 'SOC' && (valeur[0] !== 0 || valeur[1] !== 0)){
                    let min = valeur[0],
                        max = valeur[1];
                    rules.push({ champ, valeur: {'min': min, 'max': max}, operateur: "compris entre"});
                } else {
                    //Modification du format des préalertes (extraction de l'id depuis la chaine de caractère)
                    if(champ === 'PREALERTE'){
                        valeur = valeur.map(item => {
                            const match = item.match(/\(([^)]+)\)/); // Regex pour capturer l'ID entre parenthèses
                            return match ? match[1] : null; // Retourne l'ID capturé ou null si non trouvé
                        }).filter(id => id !== null); // Filtrer les éléments null si besoin
                    }
                    //Traitement des valeurs en fonction du type
                    if (Array.isArray(valeur)) {                    
                        if (valeur.length > 0 && valeur[0] !== '') {
                            rules.push({ champ, valeur, operateur: 'parmi' });
                        }
                    } else if (valeur !== '' && valeur !== null) {
                        rules.push({ champ, valeur, operateur });
                    }
                }
            };

            //Cas spécifique pour les dates, si il y en a deux elles sont regrouppées en une seule règle
            if (when.startDate && when.endDate){
                let min = moment(when.startDate).format('YYYY-MM-DD HH:mm:ss'),
                    max = moment(when.endDate).format('YYYY-MM-DD HH:mm:ss');
                addRule('HEADER_EVTDATE', {'min': min, 'max': max}, 'compris entre');
            } else if (when.startDate ){
                addRule('HEADER_EVTDATE', moment(when.startDate).format('YYYY-MM-DD HH:mm:ss'), '>');
            } else if (when.endDate){
                addRule('HEADER_EVTDATE', moment(when.endDate).format('YYYY-MM-DD HH:mm:ss'), '<');
            }

            Object.keys(criteria).forEach((key) => {
                addRule(key, criteria[key], '=');
            });

            //Cas Modification
            if (filterid){ 
                const filter = {
                    id: filterid,
                    name: generalInfo.name,
                    description: generalInfo.description,
                    rules,
                    sql_query: ''
                }

                await dispatch(filterActions.updateFilter({ filterid, filter }));
                message = 'Filtre mis à jour avec succès';
            } 
            // Cas création
            else { 
                const filter = {
                    name: generalInfo.name,
                    description: generalInfo.description,
                    rules,
                    sql_query: ''
                }
            
                await dispatch(filterActions.createFilter(filter)).unwrap();
                message = 'Filtre créé avec succès';
            }

            //Récupération de la liste des filtres mise à jour
            dispatch(filterActions.getAllFilters());

            // Redirection après validation
            let url = "/filtres";
            const { from } = history.location.state || { from: { pathname: url } };
            history.navigate(from);

            // Affichage de l'alerte de succès
            dispatch(alertActions.success({ message, showAfterRedirect: true }));

        } catch (error) {
            dispatch(alertActions.error(error));
        }
        
    };

    /**
     * Gère l'affichage des différentes sections
     * @param {*} section 
     */
    const toggleSection = (section) => {
        setSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const InputProps={
        className: 'leading-none'
    }

    return (
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="fr">
            <div className="px-10 pb-4 title-customtable">
                {title}
            </div>
            <div className='px-5'>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {/******  General Info ******/}
                    <div className="mb-2">
                        <div className="flex items-center justify-between mb-2">
                            <Typography variant="h6">Informations générales</Typography>
                            <IconButton onClick={() => toggleSection('generalInfo')}>
                                {sections.generalInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </div>
                        {sections.generalInfo && (
                        <div className='mt-3'>
                            <TextField
                                {...register('generalInfo.name')}
                                label="Nom du filtre"
                                error={!!errors.generalInfo?.name}
                                helperText={errors.generalInfo?.name?.message}
                                className='form-control w-1/2 mb-2'
                                InputProps={InputProps}
                                size="small"
                            />
                            <TextField
                                {...register('generalInfo.description')}
                                width="50%"
                                label="Description"
                                multiline
                                rows={4}
                                error={!!errors.generalInfo?.description}
                                helperText={errors.generalInfo?.description?.message}
                                className='form-control mt-2'
                                size="small"
                            />
                        </div>
                        )}
                    </div>                    

                    {/****** Criteria ******/}
                    <div className=" mt-4">
                        <div className="flex justify-between align-center">
                            <Typography variant="h6">Critères</Typography>
                            <IconButton onClick={() => toggleSection('criteria')}>
                                {sections.criteria ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </div>
                        {sections.criteria && (
                            <div>                                
                                {/****** COUNTRY ******/}
                                <InputLabel className='mb-2 mt-2' id="flotte-label" shrink={false}>
                                    Sélectionnez un ou plusieurs pays
                                </InputLabel>
                                <Controller
                                    name="criteria.COUNTRY"
                                    control={control}
                                    render={({ field }) => (
                                        <MultiSelectAutocomplete
                                            {...field}
                                            label="Pays"
                                            options={filterValues?.COUNTRY || []}
                                            onInputChange={(value) => field.onChange(value)}
                                            onClear={() => field.onChange([])}                                            
                                        />
                                    )}
                                />
                                {/****** CITY ******/}
                                <InputLabel className='mb-2 mt-3' id="flotte-label" shrink={false}>
                                    Sélectionnez une ou plusieurs flotte(s)
                                </InputLabel>
                                <Controller
                                    name="criteria.CITY"
                                    control={control}
                                    render={({ field }) => (
                                        <MultiSelectAutocomplete
                                            {...field}
                                            label="Flotte"
                                            options={filterValues?.CITY || []}
                                            onInputChange={(value) => field.onChange(value)}
                                            onClear={() => field.onChange([])}
                                        />
                                    )}
                                />
                                {/****** BUS TYPE ******/}
                                {/*<InputLabel className='mb-2 mt-3' id="bus-label" shrink={false}>
                                    Sélectionnez un plusieurs ou type(s) de véhicule
                                </InputLabel>
                                <Controller
                                    name="criteria.busType"
                                    control={control}
                                    render={({ field }) => (
                                        <MultiSelectAutocomplete
                                            {...field}
                                            label="Type de bus"
                                            options={["GX137C(t)", "GX337_12ml(t)", "Crossway(t)"]}
                                            onInputChange={(value) => field.onChange(value)}
                                            onClear={() => field.onChange([])}
                                            disabled={true}
                                        />
                                    )}
                                />*/}
                                {/****** BATTERY TYPE ******/}
                                <InputLabel className='mb-2 mt-3'  id="bus-label" shrink={false}>
                                    Sélectionnez un ou type(s) de batterie
                                </InputLabel>
                                <Controller
                                    name="criteria.BATTERY_MODEL"
                                    control={control}
                                    render={({ field }) => (
                                        <MultiSelectAutocomplete
                                            {...field}
                                            label="Type de batterie"
                                            options={filterValues?.BATTERY_MODEL || []}
                                            onInputChange={(value) => field.onChange(value)}
                                            onClear={() => field.onChange([])}
                                            sxOverrides={{
                                            }}
                                        />
                                    )}
                                />
                                {/****** PACK NB ******/}
                                <InputLabel className='mb-2 mt-3' id="bus-label" shrink={false}>
                                    Sélectionnez le nombre de packs
                                </InputLabel>
                                <Controller
                                    name="criteria.PACKS_NB"
                                    control={control}
                                    render={({ field }) => (
                                        <MultiSelectAutocomplete
                                            {...field}
                                            label="Nombre de pack"
                                            options={filterValues?.PACKS_NB || []}
                                            onInputChange={(value) => field.onChange(value)}
                                            onClear={() => field.onChange([])}
                                        />
                                    )}
                                />
                                {/****** CASE TYPE ******/}
                                <InputLabel className='mb-2 mt-3' id="bus-label" shrink={false}>
                                    Sélectionnez le(s) type(s) de boitier
                                </InputLabel>
                                <Controller
                                    name="criteria.ECU_STATIC"
                                    control={control}
                                    render={({ field }) => (
                                        <MultiSelectAutocomplete
                                            {...field}
                                            label="Type de boitier"
                                            options={filterValues?.ECU_STATIC || []}
                                            onInputChange={(value) => field.onChange(value)}
                                            onClear={() => field.onChange([])}
                                        />
                                    )}
                                />
                            </div>                            
                        )}
                    </div>

                    {/****** ALERTS and SOC ******/}
                    <div className='mt-4' >
                        <div className="flex justify-between align-center">
                            <Typography variant="h6">Alertes et SOC</Typography>
                            <IconButton onClick={() => toggleSection('alert')}>
                                {sections.alert ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </div>
                        {sections.alert && (
                            <>
                                <div className="flex space-between w-2/3">    
                                    {/****** LEVELS ******/}                                
                                    <div  className="flex flex-col basis-1/2">                                        
                                        <InputLabel className='mb-2 mt-2' id="level-label" shrink={false}>
                                            Sélectionnez un ou plusieurs niveaux
                                        </InputLabel>
                                        <Controller
                                            name="criteria.URGENCY_LEVEL"
                                            control={control}
                                            defaultValue={[]}
                                            render={({ field }) =>
                                                ["0", "1", "2", "3", "4"].map((level) => (
                                                <FormControlLabel
                                                    key={level}
                                                    control={
                                                    <Checkbox
                                                        checked={field.value.includes(level)} // Vérifie si le type est inclus dans les valeurs sélectionnées
                                                        onChange={(e) => {
                                                        const isChecked = e.target.checked;
                                                        const updatedTypes = isChecked
                                                            ? [...field.value, level] // Ajoute le type si coché
                                                            : field.value.filter((value) => value !== level); // Retire le type s'il est décoché
                                                        field.onChange(updatedTypes);
                                                        }}
                                                    />
                                                    }
                                                    label={`Niveau ${level}`}
                                                />
                                                ))
                                            }
                                        />
                                    </div>
                                    {/****** SOC ******/}
                                    <div className="flex flex-col basis-1/2">
                                        <InputLabel className='mb-4 mt-2' id="soc-label" shrink={false}>
                                            Valeur du SOC comprise entre<br/>
                                            <small>(Laissez à 0 pour ne pas prendre en compte)</small>
                                        </InputLabel>
                                        <Controller
                                            name="criteria.SOC"
                                            control={control}
                                            render={({ field }) => (
                                                <Slider
                                                    {...field}    
                                                    value={field.value}                                        
                                                    onChange={(e, value) => field.onChange(value)}
                                                    defaultValue={[20, 80]}
                                                    valueLabelDisplay="on"
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    marks={marks}
                                                    size="small"
                                                    className='form-control w-1/2'
                                                    getAriaValueText={valuetext}
                                                />
                                            )}
                                        />
                                    </div>                            
                                </div>
                                {/****** TIME RANGE ******/}
                                <div className="mb-2">
                                    <InputLabel className='mb-2 mt-3' id="time-label" shrink={false}>
                                        Sélectionnez un interval de temps
                                    </InputLabel>                       
                                    <div  className="flex flex-col w-3/4 mb-2 justify-between">
                                        <div className="row w-full">
                                            <div className='w-[40%]'>
                                                <label className="form-label mb-2">Entre le</label>
                                            </div>
                                            <div className='w-[40%]'>
                                                <label className="form-label mb-2">et le</label>
                                            </div>
                                        </div>                                
                                        <div className="row w-full mt-2">                                    
                                            <div className='w-[40%]'>
                                                <Controller
                                                    name={`when.startDate`}
                                                    control={control}                                                 
                                                    render={({ field, fieldState }) => (
                                                        <DateTimePicker
                                                            label="Date de début"
                                                            value={field.value}
                                                            onChange={(date) => field.onChange(date)}                                                            
                                                            ampm={false}
                                                            minDate={oneMonthAgoMidnight}
                                                            maxDate={getValues('when.endDate') ?? todayMidnight}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    error={!!fieldState.error}
                                                                    helperText={fieldState.error ? fieldState.error.message : null}
                                                                />
                                                            )}                                                    
                                                            className="form-control"                                          
                                                            viewRenderers={{
                                                                hours: renderTimeViewClock,
                                                                minutes: renderTimeViewClock,
                                                                seconds: renderTimeViewClock,
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className='w-[40%]'>                                
                                                <Controller
                                                    name={`when.endDate`}
                                                    control={control}
                                                    render={({ field, fieldState }) => (
                                                        <DateTimePicker
                                                            label="Date de fin"
                                                            value={field.value}
                                                            onChange={(date) => field.onChange(date)}                                                           
                                                            ampm={false}
                                                            minDate={getValues('when.startDate') ?? oneMonthAgoMidnight}
                                                            maxDateDate={todayMidnight}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    error={!!fieldState.error}
                                                                    helperText={fieldState.error ? fieldState.error.message : null}
                                                                />
                                                            )}
                                                            className="form-control"                                          
                                                            viewRenderers={{
                                                                hours: renderTimeViewClock,
                                                                minutes: renderTimeViewClock,
                                                                seconds: renderTimeViewClock,
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>                                                           
                                    </div>                                  
                                </div>    
                                {/****** ALERTS ******/}                                                       
                                {<><InputLabel className='mb-2 mt-3' id="flotte-label" shrink={false}>
                                    Sélectionnez une ou plusieurs pré-alerte(s)
                                </InputLabel>
                                <Controller
                                    name="criteria.PREALERTE"
                                    control={control}
                                    render={({ field }) => (
                                        <MultiSelectAutocomplete
                                            {...field}
                                            label="Pré-alerte"
                                            options={preAlertsList || []}
                                            onInputChange={(value) => field.onChange(value)}
                                            onClear={() => field.onChange([])}                                            
                                        />
                                    )}
                                /></>}
                            </>
                        )}
                    </div>
                    <button type="submit" variant="contained" className="btn-custom btn-custom-primary mt-4 mb-6 float-right">
                        <img src={getIcons("checkOutlined")} alt="icon return" className="btn-custom-icon"/>
                        Enregistrer
                    </button>
                </form>
            </div>
        </LocalizationProvider>
    );
};


